import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { submitEnquiry } from "../lib/api";

const inputCls =
  "bg-transparent border-0 border-b border-border rounded-none px-0 h-11 text-sm placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary transition-colors";

const INITIAL = { name: "", phone: "", email: "", intent: "Pre-Launch EOI", message: "" };

export default function EnquiryForm({ defaultIntent, onSuccess }) {
  const [form, setForm] = useState(() => ({ ...INITIAL, intent: defaultIntent || INITIAL.intent }));
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (defaultIntent) setForm((f) => ({ ...f, intent: defaultIntent }));
  }, [defaultIntent]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your full name");
    if (!/^\+?[\d\s-]{10,16}$/.test(form.phone.trim()))
      return toast.error("Please enter a valid 10-digit mobile number");
    setLoading(true);
    try {
      await submitEnquiry({
        ...form,
        email: form.email || undefined,
        message: form.message || undefined,
      });
      toast.success("Thank you. Our relationship team will reach out shortly.");
      setForm(INITIAL);
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please reach us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="enquiry-form" className="flex flex-col gap-7">
      <div className="grid sm:grid-cols-2 gap-7">
        <Input required value={form.name} onChange={set("name")} placeholder="Full Name *" data-testid="enquiry-name-input" className={inputCls} />
        <Input required value={form.phone} onChange={set("phone")} placeholder="Mobile Number *" data-testid="enquiry-phone-input" className={inputCls} />
      </div>
      <Input type="email" value={form.email} onChange={set("email")} placeholder="Email Address" data-testid="enquiry-email-input" className={inputCls} />
      <select
        value={form.intent}
        onChange={set("intent")}
        data-testid="enquiry-intent-select"
        className={`${inputCls} w-full cursor-pointer [&>option]:bg-white`}
      >
        {["Pre-Launch EOI", "Request Brochure", "Schedule Site Visit", "Request Call Back"].map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      <Input value={form.message} onChange={set("message")} placeholder="Message (optional)" data-testid="enquiry-message-input" className={inputCls} />
      <label className="flex items-start gap-3 text-[11px] text-muted-foreground leading-relaxed cursor-pointer">
        <input required type="checkbox" data-testid="enquiry-consent-checkbox" className="mt-0.5 accent-[#1B5FAD]" />
        <span>
          I authorise sugee.co and its representatives to contact me via call, SMS, email or WhatsApp about this
          project. This consent overrides any DNC/NDNC registration.
        </span>
      </label>
      <Button
        type="submit"
        disabled={loading}
        data-testid="enquiry-submit-button"
        className="w-full bg-primary text-primary-foreground hover:bg-[#154d8f] rounded-none h-12 text-[11px] uppercase tracking-[0.3em] transition-colors disabled:opacity-60"
      >
        {loading ? "Submitting…" : "Express Your Interest"}
      </Button>
    </form>
  );
}
